import React from "react";
import { useEffect, useCallback } from "react";
import { Box } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { COLORS } from "../assets/theme";
import CustomTable from "../components/table/CustomTable";
import SearchHeaderContainer from "../components/table/SearchHeaderContainer";
import Actions from "../components/Actions";
import GenericFormDialog, { Field } from "../components/GenericFormDialog";
import ConfirmDialog from "../components/ConfirmDialog";
import CustomLabel from "../components/CustomLabel";
import {
  getAllPromoCodes,
  createPromoCode,
  deletePromoCode,
  updatePromoCode,
} from "../services/PromoCodeService";
import { getUserId } from "../services/authService";
import { IField } from "../types/table/interfaces.type";

const PromoCode: React.FC = () => {
  const [editOpen, setEditOpen] = React.useState(false);
  const [editingRow, setEditingRow] = React.useState<any | null>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [toDeleteId, setToDeleteId] = React.useState<string | number | null>(
    null
  );

  const [search, setSearch] = React.useState<string>("");

  const getToday = () => new Date().toISOString().split("T")[0];

  const normalizeDateForDisplay = (value: any) => {
    if (!value) return "";
    const match = String(value).match(/^\d{4}-\d{2}-\d{2}/);
    return match ? match[0] : String(value);
  };

  const formFields: Field[] = [
    { name: "title", label: "Title" },
    { name: "value", label: "Value" },
    {
      name: "type",
      label: "Type",
      disabled: true,
    },
    { name: "max_usage", label: "Max Usage" },
    { name: "limit_per_user", label: "Limit Per User", type: "number" },
    { name: "start_date", label: "Start Date", type: "date" },
    { name: "expiry_date", label: "Expiry Date", type: "date" },
  ];

  const openEditForId = (id: string | number) => {
    const found = rows.find((r: any) => String(r.id) === String(id));
    if (found) {
      setEditingRow(found);
      setEditOpen(true);
    }
  };
  const wiredActions = Actions({
    handleOpenEditModal: { handler: openEditForId },
    onDelete: {
      handler: async (id: number | string) => {
        setToDeleteId(id);
        setConfirmOpen(true);
      },
    },
  });

  const [rows, setRows] = React.useState<any[]>([]);

  const refreshData = useCallback(async () => {
    try {
      const currentUserId = getUserId();
      if (!currentUserId) {
        console.error("Cannot fetch promo codes: missing user ID");
        setRows([]);
        return;
      }
      const parsedUserId = Number(currentUserId);
      const data = (await getAllPromoCodes(
        Number.isNaN(parsedUserId) ? currentUserId : parsedUserId
      )) as { promo_codes?: any[] };

      setRows(data.promo_codes || []);
    } catch (error) {
      console.error("Failed to fetch promo codes", error);
    }
  }, []);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const fetchFunc = useCallback(
    async (params?: any) => {
      let content = [...rows];

      const q = (params && params.search) || "";
      const normalizedQuery = q.toLowerCase().trim();
      if (normalizedQuery) {
        content = content.filter((r: any) => {
          const haystack = [
            r.title,
            r.value,
            r.id,
            r.max_usage,
            r.usage_count,
            r.limit_per_user,
            r.start_date,
          ]
            .filter(Boolean)
            .map((v: any) => String(v).toLowerCase())
            .join(" ");
          return haystack.includes(normalizedQuery);
        });
      }

      return {
        content,
        number: 0,
        size: (params && params.size) || 10,
        totalElements: content.length,
        totalPages: Math.max(
          1,
          Math.ceil(content.length / ((params && params.size) || 10))
        ),
      };
    },
    [rows]
  );

  return (
    <Box sx={{ width: "100%", maxWidth: "90vw", overflowX: "hidden" }}>
      <Box sx={{ mt: { xs: 2, sm: 3 } }}>
        <CustomLabel
          text="Promo Codes"
          variant="h4"
          fontWeight="semi-bold"
          sx={{ mb: 2 }}
        />
        <SearchHeaderContainer
          search={search}
          setSearch={setSearch}
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.MainColor,
              startIcon: <AddIcon />,
              onClick: () => {
                setEditingRow(null);
                setEditOpen(true);
              },
              textColor: COLORS.WhiteText,
              minHeight: 36,
              maxHeight: 36,
              sx: {
                width: { xs: "96%", sm: 36 },
                minWidth: { sm: 36 },
                marginRight: { xs: 1.5, sm: 0 },
              },
            },
          ]}
        />
        <CustomTable<any>
          filters={{ page: 0, size: 10, search }}
          clientSidePagination={true}
          fields={[
            { key: "title", header: "Title" },
            { key: "value", header: "Value" },
            { key: "type", header: "Type" },
            { key: "usage_count", header: "Usage Count" },
            { key: "max_usage", header: "Max Usage" },
            { key: "limit_per_user", header: "Limit Per User" },
            {
              key: "start_date",
              header: "Start Date",
              transform: (row: any) => normalizeDateForDisplay(row.start_date),
            },
            {
              key: "expiry_date",
              header: "Expiry Date",
              transform: (row: any) => normalizeDateForDisplay(row.expiry_date),
            },
            // { key: "created_at", header: "Created At" },
            // { key: "updated_at", header: "Updated At" },
          ]}
          actions={wiredActions}
          fetchFunc={fetchFunc}
        />
        <GenericFormDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          title={editingRow ? `Edit Promo Code` : "Add Promo Code"}
          fields={editingRow ? formFields : [...formFields]}
          initialValues={
            editingRow
              ? Object.keys(editingRow).reduce((acc: any, key) => {
                  const val = editingRow[key];
                  acc[key] =
                    val === undefined || val === null ? "" : String(val);
                  return acc;
                }, {})
              : { type: "percent", start_date: getToday() }
          }
          onSubmit={async (values) => {
            const today = getToday();
            if (editingRow) {
              const userId = getUserId();
              if (!userId) {
                console.error("Cannot update promo code: missing user ID");
                return;
              }
              const parsedUserId = Number(userId);
              const sanitizedValues = {
                ...values,
              };
              if (sanitizedValues.limit_per_user === "") {
                delete sanitizedValues.limit_per_user;
              }
              const startDate =
                sanitizedValues.start_date || editingRow.start_date || today;
              const payload = {
                ...sanitizedValues,
                start_date: startDate,
                user_id: Number.isNaN(parsedUserId) ? userId : parsedUserId,
                updated_at: today,
              };
              try {
                await updatePromoCode(String(editingRow.id), payload);
                await refreshData();
              } catch (error) {
                console.error("Failed to update promo code", error);
                return;
              }
            } else {
              const userId = getUserId();
              if (!userId) {
                console.error("Cannot create promo code: missing user ID");
                return;
              }
              const parsedUserId = Number(userId);
              const sanitizedValues = {
                ...values,
              };
              if (sanitizedValues.limit_per_user === "") {
                delete sanitizedValues.limit_per_user;
              }
              const startDate = sanitizedValues.start_date || today;
              const payload = {
                ...sanitizedValues,
                start_date: startDate,
                user_id: Number.isNaN(parsedUserId) ? userId : parsedUserId,
              };
              try {
                await createPromoCode(payload);
                // Refresh data from API to get the latest list
                await refreshData();
              } catch (error) {
                console.error("Failed to create promo code", error);
              }
            }
            setEditOpen(false);
          }}
        />
        <ConfirmDialog
          open={confirmOpen}
          onClose={() => {
            setConfirmOpen(false);
            setToDeleteId(null);
          }}
          title="Delete entry"
          message="Are you sure you want to delete this entry? This action cannot be undone."
          onConfirm={async () => {
            if (toDeleteId === null) return;
            try {
              await deletePromoCode(String(toDeleteId));
              setRows((prev) =>
                prev.filter((r: any) => String(r.id) !== String(toDeleteId))
              );
            } catch (error) {
              console.error("Failed to delete promo code", error);
            }
            setConfirmOpen(false);
            setToDeleteId(null);
          }}
        />
      </Box>
    </Box>
  );
};

export default PromoCode;
